// src/encounter/lib/soap-ribbon.ts
//
// PROD-2743 · frame v2 (DARK behind `isEncounterFlagOn('frameV2')`). The S·O·A·P ribbon GROUPS the
// encounter's EXISTING steps under the note's sections. It adds no step, removes no step and
// reorders nothing: the steps still come from the role-filtered block catalog (`resolveSteps`), so
// every persona keeps exactly the steps it has today (physician all seven; nurse without A/P;
// allied health Risk · Doc · Plan · Wrap-up). This file only answers "which section does this step
// sit in", as data, and it is the ONE answer: the ribbon (`groupForRibbon`), the stacked canvas
// (`sectionStepsFor`) and the page's leave-a-section completion (`sectionOf`) all read it, so the
// three can never disagree.
//
// The track is the approved design's (R6c Patient Workspace, `nav[aria-label="Note sections"]`):
// there is NO Wrap-up tab. Wrap-up is the LAST BLOCK of the note's final section, and that section
// shows as active while you are in it (design: `activeL = cur === 'W' ? 'P' : cur`).
//   S    = HPI · Risk · ROS
//   O    = PE · Doc
//   A    = physician: the A/P step's ASSESSMENT part (narrative + confirmed diagnoses)
//   P    = physician: the A/P step's PLAN part (orders under their diagnosis, the care plan,
//          education), then the Wrap-up block ("Plan").
//          allied health: the Plan step, then Wrap-up ("Plan"). No A: allied health documents a plan
//          and enters no medical diagnosis, so it never sits under an "Assessment" heading (PROD-2040;
//          the mapping is the encounter owner's). [PROD-3376, encounter owner 2 Nov] Narrowed: a template
//          section coded LOINC 51848-0 (the dietitian's NUTRITION diagnosis, the coach's readiness) adds
//          an Assessment section holding that card only — `extraSections`, lib/allied-soap.ts.
//          nurse: no A/P and no plan step, so P holds the Wrap-up block alone ("Nursing plan").
//
// TS-3 (Samer A6, 30 Sep: "S · O · A · P, with A and P separate"; D-52 "Wrap-up is the last block of
// P"). The A/P is ONE step (one engine, one PatientAssessmentPlan save, one Send Orders): it is not
// split in two. It is RENDERED in two parts — its assessment part in A and its plan part in P
// (`SoapSection.apPart`) — so the step model, the save path and the OFF nav are untouched. The
// step's navigation home is P (`sectionOf('ap') === 'plan'`): every place the page moves to the A/P
// itself (Add Order, an ambient or order-set order) is about orders. Standing in A is the view's
// own state (encounter-shell-view.tsx).
//
// The ribbon SWITCHES sections (note mode `sections`), or JUMPS to them in one continuous scroll
// (note mode `scroll`, TS-3, the frame v2 default); a section renders every step it holds as a block.
import { ALLIED_PLAN_STEP, NURSING_PLAN_STEP, type PaStepKey } from './encounter-steps';

export type SoapSectionId = 's' | 'o' | 'a' | 'plan';

/** Which part of the ONE A/P step a section draws (TS-3): the assessment in A, the plan in P. */
export type ApPart = 'assessment' | 'plan';

export interface SoapSectionDef {
  id: SoapSectionId;
  /** The letter set before the label on the tab (mono 11 bold in the approved track). */
  letter: string;
  /** The tab's label, and its accessible name after the letter. */
  title: string;
}

/**
 * The section each step belongs to by its own nature. Keyed exhaustively over `PaStepKey`, so a new
 * step key cannot ship without a ruling. `null` is Wrap-up's: it has no section of its own and
 * closes whichever section ends the note (`sectionOf`).
 */
const OWN_SECTION: Record<PaStepKey, SoapSectionId | null> = {
  hpi: 's',
  risk: 's',
  ros: 's',
  pe: 'o',
  docs: 'o',
  ap: 'plan',
  [ALLIED_PLAN_STEP]: 'plan',
  // [PROD-2949] The nurse's plan block opens P, before the Wrap-up block (design desk D-66).
  [NURSING_PLAN_STEP]: 'plan',
  'wrap-up': null,
};

const SECTION_DEFS: Record<SoapSectionId, SoapSectionDef> = {
  s: { id: 's', letter: 'S', title: 'Subjective' },
  o: { id: 'o', letter: 'O', title: 'Objective' },
  a: { id: 'a', letter: 'A', title: 'Assessment' },
  plan: { id: 'plan', letter: 'P', title: 'Plan' },
};

/** The nurse's P section's title when it holds the nursing plan block (`?ef=planSave` on; design `LABELS`). */
export const NURSING_PLAN_TITLE = 'Nursing plan';

/** The sections that can close the note, and so take the Wrap-up block. */
const CLOSING: ReadonlySet<SoapSectionId> = new Set<SoapSectionId>(['plan']);

/**
 * THE section of `step` in this nav. Every step but Wrap-up has its own (`OWN_SECTION`); the A/P's
 * is P, its navigation home (its assessment part is ALSO drawn in A — `groupForRibbon`). Wrap-up joins
 * P; with no P step at all (the nurse), it is the P section by itself, titled "Nursing plan".
 *
 * Membership depends only on whether `ap` / `allied-plan` are in the nav, and neither can be hidden
 * by a template (`HIDEABLE_STEP_KEYS` is hpi · risk · ros · pe) nor by the empty-section rule (it
 * fails open on non-questionnaire steps), so the page's nav and the view's nav agree on it.
 */
export function sectionOf(nav: readonly PaStepKey[], step: PaStepKey): SoapSectionId {
  const own = OWN_SECTION[step];
  if (own) return own;
  for (let i = nav.length - 1; i >= 0; i -= 1) {
    const s = OWN_SECTION[nav[i]];
    if (s && CLOSING.has(s)) return s;
  }
  return 'plan';
}

export interface SoapSection<T> extends SoapSectionDef {
  items: T[];
  /** The part of the A/P step this section draws, when it holds the A/P (physician A and P). */
  apPart?: ApPart;
}

/**
 * Group already-ordered steps into ribbon sections, in order of first appearance. The input order
 * is the block catalog's priority order, so S · O · A&P/P falls out of it; nothing here sorts,
 * except that Wrap-up is always the LAST block of the section it joins. A section with no visible
 * step renders no tab (a nurse gets no A&P).
 */
export function groupForRibbon<T>(
  items: readonly T[],
  stepOf: (item: T) => PaStepKey,
  /** [PROD-3376] Sections the template adds with no step of their own (allied health's coded Assessment, lib/allied-soap.ts). */
  extraSections: readonly SoapSectionId[] = [],
): SoapSection<T>[] {
  const nav = items.map(stepOf);
  const groups: SoapSection<T>[] = [];
  const wrapUps: { id: SoapSectionId; item: T }[] = [];
  const groupFor = (id: SoapSectionId): SoapSection<T> => {
    // TS-3: A always precedes P when the nav holds the A/P, whatever order the nav lists Wrap-up in.
    if (id === 'plan' && nav.includes('ap') && !groups.some((g) => g.id === 'a')) groupFor('a');
    let group = groups.find((g) => g.id === id);
    if (!group) {
      group = { ...SECTION_DEFS[id], items: [] };
      groups.push(group);
    }
    return group;
  };
  items.forEach((item, i) => {
    const id = sectionOf(nav, nav[i]);
    if (nav[i] === 'ap') {
      // TS-3: the ONE A/P step, drawn twice — its assessment part in A, then its plan part in P.
      const a = groupFor('a');
      a.items.push(item);
      a.apPart = 'assessment';
      const p = groupFor('plan');
      p.items.push(item);
      p.apPart = 'plan';
      return;
    }
    if (OWN_SECTION[nav[i]] === null) {
      // Reserve the section's place (so the nurse's P sits where Wrap-up sat), fill it last.
      groupFor(id);
      wrapUps.push({ id, item });
    } else {
      groupFor(id).items.push(item);
    }
  });
  for (const { id, item } of wrapUps) groupFor(id).items.push(item);
  // [PROD-3376] An added section takes its SOAP place (S · O · A · P) and holds no step: its cards are the template's.
  const ORDER: SoapSectionId[] = ['s', 'o', 'a', 'plan'];
  for (const id of extraSections) {
    if (groups.some((g) => g.id === id)) continue;
    const at = groups.findIndex((g) => ORDER.indexOf(g.id) > ORDER.indexOf(id));
    const added: SoapSection<T> = { ...SECTION_DEFS[id], items: [] };
    if (at < 0) groups.push(added);
    else groups.splice(at, 0, added);
  }
  // D-71 (design desk, 6 Oct): P and its tab are named by their SOAP slot, never by their only block. "Plan"
  // for the physician, allied health and the nurse with `?ef=planSave` off (P = the Wrap-up block alone);
  // "Nursing plan" only when P holds the nurse's nursing plan block ([PROD-2949], planSave on). Nothing is
  // titled "Wrap-up" but the Wrap-up block itself.
  for (const g of groups) {
    if (g.id === 'plan' && g.items.some((it) => stepOf(it) === NURSING_PLAN_STEP)) g.title = NURSING_PLAN_TITLE;
  }
  return groups;
}

/** The nav's sections, each with the steps it holds (the same answer the ribbon draws). */
export function sectionsFor(nav: readonly PaStepKey[], extraSections: readonly SoapSectionId[] = []): SoapSection<PaStepKey>[] {
  return groupForRibbon(nav, (s) => s, extraSections);
}

/**
 * The A/P step's label with the staged-order count: "A/P · 3 pending" while orders are staged but
 * not sent, the plain label otherwise. N comes from the page's existing order status (orders not
 * yet sent); nothing is fetched for it. Only the physician A/P step carries it — no other step
 * places orders.
 */
export function withPendingCount(step: PaStepKey, label: string, pendingOrderCount: number | undefined): string {
  if (step !== 'ap' || !pendingOrderCount || pendingOrderCount <= 0) return label;
  return `${label} · ${pendingOrderCount} pending`;
}

// ── TS-3 · one SOAP scroll (Samer, Loom 5 Oct 2026: "TS-3 … Can we fix this with a priority?") ──
// The approved design (R6c Patient Workspace, `noteMode`, `:797-810` and `:1249-1267`) has two note
// modes: one section at a time (the track SWITCHES sections) and one continuous scroll (the track is
// JUMP LINKS and the section in view is highlighted). Design desk ruling D-64 (5 Oct): one continuous
// scroll is the ONLY mode clinicians see — no toggle, no remembered preference. `sections` stays for
// developers only, behind the `noteSections` encounter switch.

/** How the note is laid out: every section in one scroll, or one section at a time. */
export type NoteMode = 'scroll' | 'sections';

/** Frame v2's note mode for every clinician (TS-3, Samer 5 Oct; D-64). */
export const DEFAULT_NOTE_MODE: NoteMode = 'scroll';

/** A rendered section's top edge, in px below the top of the visible scroll area (negative = above). */
export interface SectionTop {
  id: SoapSectionId;
  top: number;
}

/**
 * The section in view, from where each section's top edge sits in the scroll area — the design's
 * rule (`onScroll`, `:799-806`): the LAST section whose top has reached `line` px below the top of
 * the visible area; once the area is scrolled to its end, the last section (a short final section
 * can never reach the line, and it must still be reachable). Before any section reaches the line,
 * the first one. `tops` are in document order. Empty → `undefined`.
 */
export function sectionInView(tops: readonly SectionTop[], line: number, atEnd: boolean): SoapSectionId | undefined {
  if (tops.length === 0) return undefined;
  if (atEnd) return tops[tops.length - 1].id;
  let current = tops[0].id;
  for (const t of tops) if (t.top <= line) current = t.id;
  return current;
}

/** The first working step of section `id` in this nav (what a section tab goes to), if it has one. */
export function firstStepOfSection(nav: readonly PaStepKey[], id: SoapSectionId): PaStepKey | undefined {
  return sectionsFor(nav).find((g) => g.id === id)?.items[0];
}

/**
 * Frame v2 (contract §1a, sheet v1.6): the working steps of the S·O·A·P section that holds
 * `current`, in the order the ribbon holds them (Wrap-up last). The section renders all of them as
 * blocks in one scroll; the ribbon switches sections, never steps. Never empty, and never without
 * `current`: a step the nav does not hold stands alone (Codex P2, 1 Oct — it used to return the
 * rest of its section and drop the step itself). Unreachable from the view (`visibleSteps` always
 * keeps the step you stand on) and the page (`useCurrentStep` reconciles into the nav), so this is
 * the contract, not a live path.
 */
export function sectionStepsFor(nav: readonly PaStepKey[], current: PaStepKey): PaStepKey[] {
  if (!nav.includes(current)) return [current];
  const id = sectionOf(nav, current);
  const section = sectionsFor(nav).find((g) => g.id === id);
  return section && section.items.length > 0 ? section.items : [current];
}
